import type { Drill } from './drill-types';
import { getTagValues } from '@/utils/drill-taxonomy';

const POSTURAL_FILTER_KEY = 'Posture';

/** Same rules as DrillLibrary filteredDrills / drillMatchesFiltersExcluding. */
export function postureValuesMatch(filterVal: string, drillVal: string): boolean {
  if (filterVal === drillVal) return true;
  if (
    filterVal === 'Anterior Orientation' &&
    (drillVal === 'Anterior Orientation' || drillVal === 'Anterior Pelvic Tilt (APT)')
  ) {
    return true;
  }
  if (filterVal.replace(/[-‑–—]/g, '-') === drillVal.replace(/[-‑–—]/g, '-')) {
    return true;
  }
  return false;
}

/**
 * Whether this single selected filter value is satisfied by the drill's tags for that category.
 * Posture uses flexible matching; other categories use exact tag equality.
 */
export function drillSatisfiesFilterValue(drill: Drill, category: string, filterVal: string): boolean {
  const drillValues = getTagValues(drill, category);
  if (drillValues.length === 0) return false;

  if (category === POSTURAL_FILTER_KEY) {
    return drillValues.some((drillVal) => postureValuesMatch(filterVal, drillVal));
  }

  return drillValues.includes(filterVal);
}

/** Posture: ALL selected values must match (AND). Empty set is vacuously true. */
export function drillSatisfiesPostureFilterSet(drill: Drill, filterValues: Set<string>): boolean {
  if (filterValues.size === 0) return true;
  const drillValues = getTagValues(drill, POSTURAL_FILTER_KEY);
  if (drillValues.length === 0) return false;
  return Array.from(filterValues).every((filterVal) =>
    drillValues.some((drillVal) => postureValuesMatch(filterVal, drillVal))
  );
}

/** Non-posture category: ANY selected value may match (OR within category). */
export function drillSatisfiesAnyFilterValue(drill: Drill, category: string, filterValues: Set<string>): boolean {
  if (filterValues.size === 0) return true;
  return Array.from(filterValues).some((filterVal) => drillSatisfiesFilterValue(drill, category, filterVal));
}

/**
 * Phase + Domain row: AND across categories (each active primary dimension must match),
 * OR within a category (e.g. Phase 1 or Phase 2 selected → drill may match either).
 */
export function drillSatisfiesPrimaryFilterKeys(
  drill: Drill,
  primaryKeys: string[],
  activeFilters: { [key: string]: Set<string> }
): boolean {
  if (primaryKeys.length === 0) return true;
  return primaryKeys.every((key) => drillSatisfiesAnyFilterValue(drill, key, activeFilters[key]!));
}
